import { AnimatePresence } from 'motion/react'
import { useEffect, useState } from 'react'
import { preload } from 'react-dom'
import imgHero from '~/assets/images/hero.webp'
import { InfiniteCarousel } from '~/components/infinite-carousel'
import { m } from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime'
import { CtaBubble } from './components/cta-bubble'
import { AppIcon } from './components/icon/app-icon'
import { Layout } from './components/layout'
import { Page } from './components/page'
import { Reveal } from './components/reveal'
import { native } from './services/native'

const carousel1Images = Object.values(
  import.meta.glob('./assets/images/carousel-1/*.png', {
    eager: true,
    query: '?url',
    import: 'default',
  }) as Record<string, string>,
)

const carousel2Images = Object.values(
  import.meta.glob('./assets/images/carousel-2/*.png', {
    eager: true,
    query: '?url',
    import: 'default',
  }) as Record<string, string>,
)

const page3Images = import.meta.glob('./assets/images/page-3/*.png', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>

function getLocalizedPage3Image(index: 1 | 2) {
  const locale = getLocale()
  const localeVariants = [
    locale,
    locale.replace(/_/g, '-'),
    locale.replace(/-/g, '_'),
  ]

  for (const variant of localeVariants) {
    const image = page3Images[`./assets/images/page-3/${variant}-${index}.png`]
    if (image) {
      return image
    }
  }

  return page3Images[`./assets/images/page-3/en-${index}.png`]
}

type PageNumber = 1 | 2 | 3 | 4

const pageNameByIndex = [
  'vibe_reader_ad_1',
  'vibe_reader_ad_2',
  'vibe_reader_ad_3',
  'vibe_reader_ad_4',
] as const

export function App() {
  // const [pageIndex, setPageIndex] = useQueryState(
  //   'pageIndex',
  //   parseAsInteger.withDefault(0),
  // )
  const page3Image1 = getLocalizedPage3Image(1)
  const page3Image2 = getLocalizedPage3Image(2)

  preload(page3Image1, { as: 'image' })
  preload(page3Image2, { as: 'image' })

  const [pageIndex, setPageIndex] = useState(0)

  useEffect(() => {
    native.general.logEvent({
      event_name: 'page_view',
      event_params: {
        page_name: pageNameByIndex[pageIndex],
      },
    })
  }, [pageIndex])

  const logButtonClick = (
    componentName:
      | `vibe_reader_ad_page_${PageNumber}_btn`
      | 'vibe_reader_ad_page_1_secondary_btn'
      | 'vibe_reader_ad_page_2_secondary_btn',
  ) => {
    native.general.logEvent({
      event_name: 'component_event',
      event_params: {
        component_name: componentName,
        app_action: 'click',
        type: 'btn',
      },
    })
  }

  const handleMainButtonClick = (pageNumber: PageNumber) => {
    logButtonClick(`vibe_reader_ad_page_${pageNumber}_btn`)
    nextPage()
  }

  const handleSecondaryButtonClick = (pageNumber: 1 | 2) => {
    logButtonClick(`vibe_reader_ad_page_${pageNumber}_secondary_btn`)
    native.general.close()
  }

  const nextPage = () => {
    setPageIndex((index) => {
      if (index === 3) {
        native.general.openURL(
          'https://apps.apple.com/app/apple-store/id6748338132?pt=690486&ct=forestapp&mt=8',
        )
        return 3
      }
      return index + 1
    })
  }

  return (
    <Layout className="h-full">
      <AnimatePresence mode="wait">
        {pageIndex === 0 && (
          <Page
            pageIndex={pageIndex}
            next={() => {
              handleMainButtonClick(1)
            }}
            key="page-1"
            className="bg-[linear-gradient(to_bottom,#001F24_45%,#010921_100%)] pt-0"
            buttonText={m.identity_cta()}
            dismissText={m.identity_dismiss()}
            onSecondaryClick={() => {
              handleSecondaryButtonClick(1)
            }}
          >
            <Reveal order={1} noY>
              <div className="relative h-[47vh] w-full">
                <img
                  src={imgHero}
                  alt=""
                  className="absolute left-1/2 top-0 block h-full min-w-[100vw] -translate-x-1/2 object-cover"
                />
              </div>
            </Reveal>

            <div className="mt-[42px] flex flex-col gap-6 text-white">
              <Reveal order={2}>
                <p className="text-2xl">{m.identity_headline()}</p>
              </Reveal>

              <Reveal order={3}>
                <p className="text-xl">{m.identity_subheadline()}</p>
              </Reveal>
            </div>
          </Page>
        )}

        {pageIndex === 1 && (
          <Page
            pageIndex={pageIndex}
            next={() => {
              handleMainButtonClick(2)
            }}
            key="page-2"
            className="pt-0"
            buttonText={m.problem_cta()}
            dismissText={m.problem_dismiss()}
            onSecondaryClick={() => {
              handleSecondaryButtonClick(2)
            }}
          >
            <Reveal order={1} noY className="-mx-8">
              <div className="flex flex-col gap-6 pb-[58px] pt-[122px]">
                <InfiniteCarousel
                  images={carousel1Images}
                  gap={12}
                  className="h-[161px]"
                  duration={20}
                />
                <InfiniteCarousel
                  images={carousel2Images}
                  gap={8}
                  className="h-[98px]"
                  duration={15}
                />
              </div>
            </Reveal>

            <div className="flex flex-col gap-6 pt-9 text-white">
              <Reveal order={2}>
                <p className="text-2xl">{m.problem_headline()}</p>
              </Reveal>

              <Reveal order={3}>
                <div className="text-xl">
                  <p>{m.problem_intro()}</p>
                  <ul className="list-none pl-0">
                    <li className="flex gap-3">
                      <span className="relative bottom-[2px] shrink-0">✦</span>
                      {m.problem_pain_1()}
                    </li>
                    <li className="flex gap-3">
                      <span className="relative bottom-[2px] shrink-0">✦</span>
                      {m.problem_pain_2()}
                    </li>
                  </ul>
                </div>
              </Reveal>
            </div>
          </Page>
        )}

        {pageIndex === 2 && (
          <Page
            pageIndex={pageIndex}
            next={() => {
              handleMainButtonClick(3)
            }}
            key="page-3"
            className="pt-[74px]"
            shouldHideSecondary
            buttonText={m.solution_cta()}
          >
            <Reveal order={1} className="relative mx-auto h-[380px] w-[284px]">
              <div className="absolute bottom-0 right-0 box-border h-[340px] w-[157px] overflow-hidden rounded-[12px] border-[4px] border-[#729DAB]">
                <img src={page3Image2} alt="" className="size-full" />
              </div>
              <div className="absolute left-0 top-0 box-border h-[340px] w-[157px] overflow-hidden rounded-[12px] border-[4px] border-[#729DAB]">
                <img src={page3Image1} alt="" className="size-full" />
              </div>
            </Reveal>

            <Reveal order={2}>
              <div className="mt-9 text-xl text-white">
                <p>{m.solution_headline()}</p>
                <ul className="list-none pl-0">
                  <li className="flex gap-3">
                    <span className="relative bottom-[2px] shrink-0">✦</span>
                    {m.solution_benefit_1()}
                  </li>
                  <li className="flex gap-3">
                    <span className="relative bottom-[2px] shrink-0">✦</span>
                    {m.solution_benefit_2()}
                  </li>
                  <li className="flex gap-3">
                    <span className="relative bottom-[2px] shrink-0">✦</span>
                    {m.solution_benefit_3()}
                  </li>
                </ul>
              </div>
            </Reveal>
          </Page>
        )}

        {pageIndex === 3 && (
          <Page
            pageIndex={pageIndex}
            className="flex flex-col justify-center pb-48 pt-24"
            next={() => {
              handleMainButtonClick(4)
            }}
            key="page-4"
            shouldHideSecondary
            buttonText={m.closing_cta()}
            footerAddtional={
              <CtaBubble className="absolute bottom-[calc(100%+28px)] right-4 mt-9 rotate-[5deg]" />
            }
          >
            <AppIcon />
            <div className="mt-9 flex flex-col gap-9 text-2xl text-white">
              <Reveal order={1}>
                <p>{m.closing_headline()}</p>
              </Reveal>
              <Reveal order={2}>
                <ul>
                  <li>{m.closing_scene_1()}</li>
                  <li>{m.closing_scene_2()}</li>
                  <li>{m.closing_scene_3()}</li>
                </ul>
              </Reveal>
              <Reveal order={3}>
                <p className="font-bold">{m.closing_subheadline()}</p>
              </Reveal>
            </div>
          </Page>
        )}
      </AnimatePresence>
    </Layout>
  )
}
